/**
 * Sync React Flow graph state from protocol draft entities.
 */

import {
  MarkerType,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from "@xyflow/react";
import { useEffect, useMemo, useState } from "react";
import type {
  ProtocolAtom,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolGate,
} from "../../api/clinicalTrialsApiTypes";
import { layoutProtocolGraph } from "./protocolDagLayout";
import {
  buildProtocolGraph,
  type ProtocolDagEdgeData,
  type ProtocolDagNodeData,
} from "./protocolDagModel";

type FlowNode = Node<ProtocolDagNodeData>;
type FlowEdge = Edge<ProtocolDagEdgeData>;

/**
 * Build laid-out nodes/edges and keep them synced with draft data.
 *
 * Intentionally does not call fitView — viewport changes belong to explicit
 * expand/fullscreen actions so node clicks do not re-center the canvas.
 *
 * @param criteria - Draft criteria
 * @param atoms - Draft atoms
 * @param gates - Draft gates
 * @param connections - Draft connections
 * @param focusCriterionId - Optional criterion to expand from Flow drill-down
 * @returns Graph state, summary counts, and expand controls
 */
export function useProtocolDagGraph(
  criteria: ProtocolCriterion[],
  atoms: ProtocolAtom[],
  gates: ProtocolGate[],
  connections: ProtocolConnection[],
  focusCriterionId: string | null = null,
) {
  const [expandedCriterionId, setExpandedCriterionId] = useState<string | null>(
    focusCriterionId,
  );

  useEffect(() => {
    if (focusCriterionId) setExpandedCriterionId(focusCriterionId);
  }, [focusCriterionId]);
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);

  const summary = useMemo(() => {
    const execution = connections.filter((item) =>
      ["MEMBER_OF_GATE", "DEPENDS_ON", "APPLIES_IF"].includes(
        item.connection_type,
      ),
    );
    return {
      executionCount: execution.length,
      semanticCount: connections.length - execution.length,
    };
  }, [connections]);

  useEffect(() => {
    const built = buildProtocolGraph(
      criteria,
      atoms,
      gates,
      connections,
      expandedCriterionId,
    );
    setNodes(layoutProtocolGraph(built.nodes, built.edges));
    setEdges(
      built.edges.map((edge) => ({
        ...edge,
        markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
      })),
    );
  }, [
    criteria,
    atoms,
    gates,
    connections,
    expandedCriterionId,
    setNodes,
    setEdges,
  ]);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    summary,
    expandedCriterionId,
    setExpandedCriterionId,
    toggleCriterion: (criterionId: string) =>
      setExpandedCriterionId((current) =>
        current === criterionId ? null : criterionId,
      ),
  };
}
